/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { formatRecordingDateTime } from "../utils/recordingDate";
import { useTranslation } from "react-i18next";

import { type FC, useState } from "react";
import { Link } from "react-router-dom";

import { Modal } from "../Modal";
import { accountDisplayName } from "../auth/accountLogin";
import { localUsername } from "../auth/admin/localUsername";
import { Avatar, Size } from "../Avatar";
import { useRoomEncryptionSystem } from "../e2ee/sharedKeyManagement";
import { getRelativeRoomUrl } from "../utils/matrix";
import styles from "./RoomHistory.module.css";

export interface HistoryRoom {
  matrixRoomId: string;
  name: string;
  createdAt: number;
  expiresAt: number;
  expired: boolean;
  lastLeftAt?: number | null;
  activeParticipantCount?: number;
  participants: { userId: string; name: string }[];
}

export interface HistoryRecording {
  id: string;
  matrixRoomId?: string;
  date: number;
  duration?: number;
  type?: string;
  sizeBytes?: number;
}

export const RoomHistoryEntry: FC<{
  room: HistoryRoom;
  recordings: HistoryRecording[];
  currentUserId: string | null;
}> = ({ room, recordings, currentUserId }) => {
  const { t } = useTranslation();
  const [showPeople, setShowPeople] = useState(false);
  const encryption = useRoomEncryptionSystem(room.matrixRoomId);
  const name = room.name.trim() || t("instance.ui.unnamed_call");
  return (
    <article className={styles.room}>
      <div className={styles.header}>
        <Avatar id={room.matrixRoomId} name={name} size={Size.MD} />
        <h3>{name}</h3>
      </div>
      <div className={styles.details}>
        <p>
          <span>{t("instance.ui.created")}</span>
          <span>{formatRecordingDateTime(room.createdAt)}</span>
        </p>
        <p>
          <span>
            {t("instance.ui.participants", { count: room.participants.length })}
          </span>{" "}
          <span
            className={styles.participants}
            title={room.participants
              .map((p) => accountDisplayName(p.userId, p.name))
              .join(", ")}
          >
            {room.participants
              .map((p) =>
                p.userId === currentUserId
                  ? t("instance.ui.you")
                  : accountDisplayName(p.userId, p.name),
              )
              .join(", ") || t("instance.ui.no_participants")}
          </span>
          {room.participants.length > 0 && (
            <button
              type="button"
              className={styles.infoButton}
              aria-label={t("instance.ui.participant_list")}
              onClick={() => setShowPeople(true)}
            >
              ⓘ
            </button>
          )}
        </p>
        {!!room.lastLeftAt && (
          <p className={styles.departure}>
            <span>{t("instance.ui.last_left")}:</span>
            <span>{formatRecordingDateTime(room.lastLeftAt)}</span>
          </p>
        )}
      </div>
      <Modal
        title={t("instance.ui.participant_list")}
        open={showPeople}
        onDismiss={() => setShowPeople(false)}
      >
        <ul className={styles.peopleList}>
          {room.participants.map((person) => (
            <li key={person.userId}>
              <span>
                {person.userId === currentUserId
                  ? t("instance.ui.you")
                  : accountDisplayName(person.userId, person.name)}
              </span>
              <small>{localUsername(person.userId)}</small>
            </li>
          ))}
        </ul>
      </Modal>
      {!!room.activeParticipantCount && (
        <p>{t("instance.ui.call_in_progress")}</p>
      )}
      {!room.expired && (
        <Link to={getRelativeRoomUrl(room.matrixRoomId, encryption, room.name)}>
          {t("instance.ui.join_call")}
        </Link>
      )}
      {recordings.length > 0 && (
        <ul className={styles.recordings}>
          {recordings.map((record) => (
            <li key={record.id}>
              <Link
                to={`/record?v=${encodeURIComponent(record.id)}`}
                state={{ fromCallList: true }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record.type === "text"
                  ? t("instance.ui.transcript")
                  : record.type === "audio"
                    ? t("instance.ui.audio_recording")
                    : t("instance.ui.video_recording")}{" "}
                — {formatRecordingDateTime(record.date)}
              </Link>
              {record.sizeBytes !== undefined && (
                <span>
                  {" "}
                  · {(record.sizeBytes / (1024 * 1024)).toFixed(1)} MB
                </span>
              )}
              {record.duration !== undefined && (
                <span>
                  {" "}
                  ·{" "}
                  {t("instance.ui.seconds", {
                    count: Math.round(record.duration),
                  })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};
